import { formatNumber } from "@angular/common";
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { iAddressSearch } from "src/components/shared-components/interfaces/iAddress-search";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { ControlGroup } from "src/model/class/general classes/control-group";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { AddressService } from "src/service/address.service";
import { LoadControlService } from "src/service/load-control.service";
import { NetworkService } from "src/service/network.service";
import { AddressSearchComponent } from "../address-search/address-search.component";

@Component({
    selector: "app-load-control-group-form",
    templateUrl: "./load-control-group-form.component.html",
    styleUrls: ["./load-control-group-form.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoadControlGroupFormComponent implements OnInit, OnDestroy {
  @Input("controlGroup") ctrlGroup: any = null;
  private _networkServiceSubscription: Subscription = null;

  private _blockUpdate: boolean = false;
  private _addressControlGroup: Address;

  public controlGroupForm: UntypedFormGroup;
  public nameAddress: string = "";
  public toolBarColor: string = "primary";
  public maxPw: number = 10;
  public minPw: number = 1.5;
  public hourSec: number = 3600;
  public oneSec: number = 1;
  public oneMin: number = 1;
  public maxMinP1P2: number = 180;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _loadControlService: LoadControlService,
    private _modalCtrl: ModalController,
    private _networkService: NetworkService,
    private _addressService: AddressService,
    private _translateService: TranslateService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.subscribeNetworkServiceData();
  }

  ngOnDestroy() {
    this._networkServiceSubscription.unsubscribe();
  }

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  private initializeForm() {
    this.controlGroupForm = this._formBuilder.group(
      {
        name: new UntypedFormControl("", [Validators.required]),
        active: new UntypedFormControl(true, { updateOn: "change" }),
        description: new UntypedFormControl("", [Validators.required]),
        totalPowerUsedAddress: new UntypedFormControl(""),
        p0: new UntypedFormControl(3),
        p1: new UntypedFormControl(3.3),
        useP1: new UntypedFormControl(false, { updateOn: "change" }),
        p2: new UntypedFormControl(4),
        useP2: new UntypedFormControl(false, { updateOn: "change" }),
        minPower: new UntypedFormControl(2.25),
        p1P2Time: new UntypedFormControl(120), // 120 min = 2 ore
        onDelayTime: new UntypedFormControl(30),
        offDelayTime: new UntypedFormControl(5),
      },
      { updateOn: "blur" }
    );

    this.controlGroupForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(AppSettings.DEFAULT_FORM_DEBOUNCE_TIME)
      )
      .subscribe((changedFormValue) => {
        if (this.ctrlGroup !== null) this.onSave();
      });
  }

  /**
   * Called by parent class when the current object is changed
   */
  ngOnChanges() {
    this._blockUpdate = true;
    if (this.ctrlGroup) {

      /**
       * with patchValue function i update form field: there aren't expressed any field because
       * there is 1:1 relation between form field and object passed to patchValue.
       * emitEvent is set to false to prevent first emission during onChange event (during initialize form)
       */
      this.controlGroupForm.patchValue(this.ctrlGroup, {
        onlySelf: true,
        emitEvent: false,
      });
      this.controlGroupForm.patchValue(
        {
          p0: formatNumber(this.ctrlGroup.p0, "en-US", "1.2-2"),
          p1: formatNumber(this.ctrlGroup.p1, "en-US", "1.2-2"),
          p2: formatNumber(this.ctrlGroup.p2, "en-US", "1.2-2"),
          minPower: formatNumber(this.ctrlGroup.minPower, "en-US", "1.2-2"),
          //p1P2Time: this.ctrlGroup.p1P2Time / 60,
          //p2Time: this.ctrlGroup.p2Time / 60,
        },
        {
          onlySelf: true,
          emitEvent: false,
        }
      );

      if (
        this.ctrlGroup.totalPowerUsedAddress != "" &&
        this.ctrlGroup.instanceGUIDTotalPowerUsed != ""
      ) {
        this.loadAddressInfo(
          this.ctrlGroup.instanceGUIDTotalPowerUsed,
          this.ctrlGroup.totalPowerUsedAddress
        );
      } else this.nameAddress = "";
    }

    this._blockUpdate = false;
  }

  /**
   * Format the values
   * @param event
   */
  public formatNumber(event: any) {
    this.formatWithNDecimal("1.5-5");
    const timer = setTimeout(() => {
      this.formatWithNDecimal("1.2-2");
      clearTimeout(timer);
    }, 100);
  }

  /**
   * Format the values, imposing a certain number of decimal places
   * @param digitInfo number of decimal places
   */
  private formatWithNDecimal(digitInfo: string) {
    this.controlGroupForm.patchValue(
      {
        p0: formatNumber(
          Number(this.controlGroupForm.get("p0").value) < this.minPw || Number(this.controlGroupForm.get("p0").value) > this.maxPw ? this.ctrlGroup.p0 : Number(this.controlGroupForm.get("p0").value),
          "en-US",
          digitInfo
        ),
        p1: formatNumber(
          Number(this.controlGroupForm.get("p1").value) < this.minPw || Number(this.controlGroupForm.get("p1").value) > this.maxPw ? this.ctrlGroup.p1 : Number(this.controlGroupForm.get("p1").value),
          "en-US",
          digitInfo
        ),
        p2: formatNumber(
          Number(this.controlGroupForm.get("p2").value) < this.minPw || Number(this.controlGroupForm.get("p2").value) > this.maxPw ? this.ctrlGroup.p2 : Number(this.controlGroupForm.get("p2").value),
          "en-US",
          digitInfo
        ),
        minPower: formatNumber(
          Number(this.controlGroupForm.get("minPower").value) < this.minPw || Number(this.controlGroupForm.get("minPower").value) > this.maxPw ? this.ctrlGroup.minPower : Number(this.controlGroupForm.get("minPower").value),
          "en-US",
          digitInfo
        ),
      },
      {
        onlySelf: true,
        emitEvent: false,
      }
    );
  }

  public async openAddressSearch() {
    const modal = await this._modalCtrl.create({
      component: AddressSearchComponent,
      cssClass: "modal-bigger",
    });

    modal.present();

    const { data } = await modal.onWillDismiss();

    if (modal) {
      modal.dismiss();
    }

    if (data && data.selectedAddress != "") {
      this._addressControlGroup = data.selectedAddress;
      this.controlGroupForm.patchValue({
        totalPowerUsedAddress: data.selectedAddress.address,
      });
      this.ctrlGroup.instanceGUIDTotalPowerUsed =
        data.selectedAddress.instanceGUID;
      this.ctrlGroup.totalPowerUsedAddress = data.selectedAddress.address;
      this.nameAddress = data.selectedAddress.fullName;
    }
  }

  /**
   * Save new control group form calling api
   */
  async onSave() {
    if (!this._blockUpdate) {
      let controlGroup: ControlGroup = this.controlGroupForm.value;
      controlGroup = this.formValueOk(controlGroup);
      if (this.ctrlGroup.id === 0) {
        const newControlGroup: ControlGroup =
          await this._loadControlService.InsertControlGroup(controlGroup);

        this.ctrlGroup.id = newControlGroup.id;
      } else {
        const updateControlGroup: ControlGroup =
          await this._loadControlService.UpdateControlGroup(
            this.ctrlGroup.id,
            controlGroup
          );
      }
    }
  }

  private formValueOk(controlGroup: ControlGroup): ControlGroup {
    if (controlGroup.name === "") controlGroup.name = this.ctrlGroup.name;
    if (controlGroup.description === "")
      controlGroup.description = this.ctrlGroup.description;
    if (
      Number(controlGroup.p1P2Time) < this.oneMin ||
      Number(controlGroup.p1P2Time) > this.maxMinP1P2
    )
      controlGroup.p1P2Time = this.ctrlGroup.p1P2Time;
    if (
      Number(controlGroup.p0) < Number(controlGroup.minPower) ||
      Number(controlGroup.p0) > this.maxPw
    )
      controlGroup.p0 = this.ctrlGroup.p0;
    if (
      Number(controlGroup.p1) < Number(controlGroup.minPower) ||
      Number(controlGroup.p1) > this.maxPw
    )
      controlGroup.p1 = this.ctrlGroup.p1;
    if (
      Number(controlGroup.p2) < Number(controlGroup.minPower) ||
      Number(controlGroup.p2) > this.maxPw
    ) {
      controlGroup.p2 = this.ctrlGroup.p2;
    }
    let power: Number = controlGroup.p0;
    if (controlGroup.useP1) {
      power = controlGroup.p1;
    } else if (controlGroup.useP2) {
      power = controlGroup.p2;
    }
    if (
      Number(controlGroup.minPower) < this.minPw ||
      Number(controlGroup.minPower) > Number(power)
    ) {
      controlGroup.minPower = this.ctrlGroup.minPower;
    }
    if (
      Number(controlGroup.onDelayTime) < this.oneSec ||
      Number(controlGroup.onDelayTime) > this.hourSec ||
      Number(controlGroup.onDelayTime) === Math.E
    )
      controlGroup.onDelayTime = this.ctrlGroup.onDelayTime;
    if (
      Number(controlGroup.offDelayTime) < this.oneSec ||
      Number(controlGroup.offDelayTime) > this.hourSec ||
      Number(controlGroup.offDelayTime) === Math.E
    )
      controlGroup.offDelayTime = this.ctrlGroup.offDelayTime;

    controlGroup.p0 = parseFloat(controlGroup.p0.toString());
    controlGroup.p1 = parseFloat(controlGroup.p1.toString());
    controlGroup.p2 = parseFloat(controlGroup.p2.toString());
    controlGroup.minPower = parseFloat(controlGroup.minPower.toString());

    controlGroup.instanceGUIDTotalPowerUsed =
      this.ctrlGroup.instanceGUIDTotalPowerUsed;
    controlGroup.totalPowerUsedAddress = this.ctrlGroup.totalPowerUsedAddress;

    return controlGroup;
  }

  /**
   * Load the address object and its informations
   * @param instanceguid
   * @param address
   */
  public async loadAddressInfo(instanceguid: string, address: string) {
    if (instanceguid != null) {
      let addressSearchObj: iAddressSearch = {
        Instanceguid: instanceguid,
        Address: address,
        Name: "",
        Language: this._translateService.getCurrentLang(),
      };

      this._addressControlGroup =
        this._addressService.getAddressByInstanceGUIDAndAddress(
          instanceguid,
          address
        );
      if (!this._addressControlGroup || this._addressControlGroup == null) {
        await this._addressService
          .getAddressesByInstanceGUIDAndAddressAndName(addressSearchObj)
          .then((result: Array<Address>) => {
            this.nameAddress = result[0].fullName;
            this._addressControlGroup = result[0];
          });
      } else {
        this.nameAddress = this._addressControlGroup.fullName;
      }
    }
  }
}
